import { AlertProps } from '@cmg/design-system';
import { ReactNode } from 'react';

import { HashScrollLink } from '../../../../../../../../common/components/hash-scroll-link/HashScrollLink';
import { OfferingType } from '../../../../../../../../graphql';
import {
  useOfferingSidePanelMatch,
  useOfferingSidePanelSearch,
} from '../../../../../../hooks/useOfferingSidePanelMatch';
import { IndicationForm_IndicationAcknowledgementsQuery } from '../../../indication-form/components/indication-details/components/acknowledgement/graphql/__generated__';

export function getAcknowledgementStatus({
  matchingCmgEntityKeys,
  acknowledgements,
  indicationVersion,
}: {
  matchingCmgEntityKeys: readonly string[];
  acknowledgements: IndicationForm_IndicationAcknowledgementsQuery['institutionalIndicationAcknowledgements'];
  indicationVersion: string;
}) {
  const acknowledgement = acknowledgements?.find(ack =>
    matchingCmgEntityKeys.includes(ack.managerCmgEntityKey)
  );

  return {
    isNewIndication: !acknowledgement,
    isUpToDate: acknowledgement?.acknowledgedIndicationVersion === indicationVersion,
  };
}

export function useIndicationAcknowledgementBanner({
  offeringType,
  matchingCmgEntityKeys,
  indicationVersion,
  acknowledgements,
  startEditingInvestorContacts,
}: {
  offeringType: OfferingType;
  matchingCmgEntityKeys: readonly string[];
  indicationVersion: string;
  acknowledgements: IndicationForm_IndicationAcknowledgementsQuery['institutionalIndicationAcknowledgements'];
  startEditingInvestorContacts: () => void;
}): {
  alertSeverity: AlertProps['severity'];
  bannerBody: ReactNode;
  bannerTitle: string | null;
  isUpToDate: boolean;
} {
  // This hook is rendered under OrderBookSellSideRoute's `useRoutes(routes, rest)` (see
  // useOfferingSidePanelMatch.ts), which scopes `useLocation()` for everything beneath it to the
  // panel-relative `rest` alone - dropping the host page's URL prefix and the real query string.
  // Reconstruct the real pathname/search from the side-panel match/search context instead, or the
  // HashLink below navigates to a broken, panel-relative-only URL.
  const match = useOfferingSidePanelMatch();
  const search = useOfferingSidePanelSearch();
  const pathname = `${match?.url ?? ''}${match?.rest ?? ''}`;

  const { isNewIndication, isUpToDate } = getAcknowledgementStatus({
    acknowledgements,
    indicationVersion,
    matchingCmgEntityKeys,
  });

  if (isUpToDate) {
    return {
      alertSeverity: 'success',
      bannerBody: 'You have acknowledged this indication.',
      bannerTitle: null,
      isUpToDate,
    };
  }

  if (isNewIndication && [OfferingType.Ipo, OfferingType.IpoSpac].includes(offeringType)) {
    return {
      alertSeverity: 'warning',
      bannerTitle: 'Acknowledge Indication',
      bannerBody: (
        <span>
          Prospectus email will be sent to all investor contacts upon your acknowledgement.{' '}
          <HashScrollLink
            title="Add additional contacts"
            smooth
            to={`${pathname}${search}#contact-information`}
            onClick={startEditingInvestorContacts}
          >
            Add additional contacts
          </HashScrollLink>
        </span>
      ),
      isUpToDate,
    };
  }

  if (isNewIndication) {
    return {
      alertSeverity: 'warning',
      bannerTitle: 'Acknowledge Indication',
      bannerBody: 'You have not yet acknowledged this indication.',
      isUpToDate,
    };
  }

  return {
    alertSeverity: 'warning',
    bannerTitle: 'Indication has changed',
    bannerBody: 'The indication has been revised since your acknowledgement.',
    isUpToDate,
  };
}
